import React, { useRef, useEffect, useState, useCallback } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { drawTicketShape } from 'utils/canvasTicket';
import './CouponAndVoucherCell.scss';
import {
  Button,
  Checkbox,
  CheckboxChangeEvent,
  Divider,
  Grid,
  Popover,
  Tag,
} from 'antd';
import { getCssVar } from 'utils/commonUtils';
import CouponAndVoucherDetail from '../../couponAndVoucherDetail/CouponAndVoucherDetail';
import dayjs from 'dayjs';
import { SLASH_FORMAT } from 'constants/consts';
import { BackendLoyaltyItem } from 'types/dto/wallet';
import DOMPurify from 'dompurify';

export interface CouponAndVoucherCellProps {
  coupon: BackendLoyaltyItem;
  needCheckBox?: boolean;
  needUseButton?: boolean;
  isExpiredOrUsed?: boolean;
  className?: string;
  isSelected?: boolean;
  onSelectionChange?: (id: number, selected: boolean) => void;
}

const CouponAndVoucherCell: React.FC<CouponAndVoucherCellProps> = ({
  coupon,
  needCheckBox = false,
  needUseButton = false,
  isExpiredOrUsed = false,
  className,
  isSelected = false,
  onSelectionChange,
}) => {
  const { t, i18n } = useTranslation('wallet');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const screens = Grid.useBreakpoint();
  const [detailVisible, setDetailVisible] = useState(false);
  const isEnglish = i18n.language === 'en';

  const handleShowDetail = useCallback(() => {
    setDetailVisible(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailVisible(false);
  }, []);

  // Draw ticket shape on canvas
  useEffect(() => {
    const drawTicket = () => {
      if (!canvasRef.current || !containerRef.current) return;

      const container = containerRef.current;
      const canvas = canvasRef.current;
      const width = container.offsetWidth;
      const height = container.offsetHeight;

      if (width === 0 || height === 0) return;

      // High DPI support
      const scale = window.devicePixelRatio || 1;
      canvas.width = width * scale;
      canvas.height = height * scale;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.scale(scale, scale);

      drawTicketShape(ctx, {
        width,
        height,
        cornerRadius: screens.xs ? 6 : 10,
        notchRadius: screens.xs ? 12 : 18,
        notchPosition: 0.7,
        borderColor: isSelected
          ? getCssVar('--primary-color')
          : getCssVar('--vls-grey-light'),
        backgroundColor: isExpiredOrUsed
          ? getCssVar('--vls-grey-light')
          : 'white',
        borderWidth: 2,
      });
    };

    drawTicket();
    window.addEventListener('resize', drawTicket);

    const resizeObserver = new ResizeObserver(() => drawTicket());
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', drawTicket);
      resizeObserver.disconnect();
    };
  }, [isExpiredOrUsed, screens, isSelected]);

  const renderUnavailableFooter = () => {
    const isActuallyUsed = (coupon.used_at && coupon.used_at !== '') || (coupon.usage?.used && coupon.usage.used > 0);
    if (isActuallyUsed) {
      return (
        <div className="coupon-cell-unavailable-footer used">{t('used')}</div>
      );
    }
    if (dayjs(coupon.expire_at).isBefore(dayjs())) {
      const formattedDate = dayjs(coupon.expire_at).format(SLASH_FORMAT);
      return (
        <div className="coupon-cell-unavailable-footer expired">
          {t('expired', { date: formattedDate })}
        </div>
      );
    }
    return null;
  };

  const renderQuantityBadge = () => {
    if (
      !isExpiredOrUsed &&
      coupon.usage?.total !== undefined &&
      coupon.usage?.used !== undefined &&
      coupon.usage.total - coupon.usage.used > 1
    ) {
      return (
        <Tag className="quantity-badge">
          {t('leftCount', { count: coupon.usage.total - coupon.usage.used })}
        </Tag>
      );
    }
    return null;
  };

  const renderValidUntil = () => {
    if (!isExpiredOrUsed && coupon.expire_at) {
      const displayDate = dayjs(coupon.expire_at).format(SLASH_FORMAT);
      return (
        <div className="coupon-cell-valid-until">
          {t('validUntil', { date: displayDate })}
        </div>
      );
    }
    return null;
  };

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    e.stopPropagation();
    onSelectionChange?.(coupon.id, e.target.checked);
  };

  const handleCellClick = () => {
    if (!isExpiredOrUsed) {
      onSelectionChange?.(coupon.id, !isSelected);
    }
  };


  const stripHtml = (html: string) => {
    if (!html) return '';

    const cleanText = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: []
    });

    return cleanText
      .replace(/&nbsp;/g, ' ')
      .replace(/\\r\\n|\\n|\\r/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const sanitizeTnc = (html: string) => {
    if (!html) return '';

    const cleaned = html.replace(/\\r\\n/g, '');

    return DOMPurify.sanitize(cleaned, {
      ADD_TAGS: ['div'],
      ADD_ATTR: ['class']
    });
  };

  return (
    <>
      <div
        ref={containerRef}
        className={`coupon-cell ${isExpiredOrUsed ? 'coupon-cell--unavailable' : ''
          } ${className || ''}`}
        onClick={needCheckBox ? handleCellClick : undefined}
        style={needCheckBox && !isExpiredOrUsed ? { cursor: 'pointer' } : undefined}
      >
        <canvas ref={canvasRef} className="coupon-cell-canvas" />
        <div className="coupon-cell-header">
          <div className="coupon-cell-title-row">
            <div className="coupon-cell-title">
              {isEnglish ? coupon.title.en : coupon.title.zh}
            </div>
            {renderQuantityBadge()}

            <Popover
              content={
                <div className="coupon-cell-popover-content">
                  <div
                    dangerouslySetInnerHTML={{ __html: sanitizeTnc(isEnglish ? coupon.tnc.en : coupon.tnc.zh) }}
                  />
                </div>
              }
              title={t('termsAndConditions')}
              trigger="hover"
              placement="topRight"
              overlayStyle={{ maxWidth: '320px' }}
            >
              <InfoCircleOutlined className="coupon-cell-info-icon" />
            </Popover>
            {needCheckBox && (
              <Checkbox
                checked={isSelected}
                onChange={handleCheckboxChange}
                onClick={(e) => e.stopPropagation()}
                disabled={isExpiredOrUsed}
                className="discount-checkbox"
              />
            )}
          </div>
        </div>

        <div className="coupon-cell-content">
          <div className="coupon-cell-short-description">
            {isEnglish ? stripHtml(coupon.details.en) : stripHtml(coupon.details.zh)}
          </div>
        </div>

        <Divider variant="dashed" className="divider" />

        <div className="coupon-cell-footer">

          <div className="coupon-cell-footer-left">
            {isExpiredOrUsed ? renderUnavailableFooter() : renderValidUntil()}
          </div>
          {needUseButton && (
            <Button
              type="link"
              onClick={(e) => { e.stopPropagation(); handleShowDetail(); }}
              className="coupon-cell-use-now-button"
            >
              {t('useNow')}
            </Button>
          )}
        </div>
      </div>

      <CouponAndVoucherDetail
        visible={detailVisible}
        onClose={handleCloseDetail}
        coupon={coupon}
      />
    </>
  );
};

export default CouponAndVoucherCell;